import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabaseClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export const NameCorrectionRequest = ({ clinician, onUpdated }) => {
  const pending = clinician.name_correction_status === "PENDING";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState(clinician.first_name || "");
  const [lastName, setLastName] = useState(clinician.last_name || "");
  const [reason, setReason] = useState("");

  const submit = async () => {
    if (!clinician.ahpra_number) {
      toast.error("An AHPRA registration number is required before requesting a name correction.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please enter the corrected first and last name.");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from("clinicians")
      .update({
        requested_first_name: firstName.trim(),
        requested_last_name: lastName.trim(),
        name_correction_reason: reason.trim() || null,
        name_correction_status: "PENDING",
        name_correction_requested_at: new Date().toISOString(),
        name_correction_responded_at: null,
      })
      .eq("id", clinician.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) {
      toast.error("Could not submit the request: " + error.message);
      return;
    }
    onUpdated(data);
    setOpen(false);
    toast.success("Name correction request sent for AHPRA review.");
  };

  if (pending) {
    return (
      <Button type="button" size="sm" variant="outline" disabled data-testid="name-correction-pending">Name correction awaiting review</Button>
    );
  }

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} disabled={!clinician.ahpra_number} data-testid="request-name-correction-button">
        Request name correction
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="name-correction-request">
          <DialogHeader>
            <DialogTitle>Request a registered-name correction</DialogTitle>
            <DialogDescription>We compare the requested name with AHPRA before changing your public profile. AHPRA number: {clinician.ahpra_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Correct first name</Label>
              <Input value={firstName} onChange={(event) => setFirstName(event.target.value)} data-testid="name-correction-first-name" />
            </div>
            <div className="space-y-2">
              <Label>Correct last name</Label>
              <Input value={lastName} onChange={(event) => setLastName(event.target.value)} data-testid="name-correction-last-name" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Reason or supporting note <span className="font-normal text-slate-400">(optional)</span></Label>
            <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="For example: legal name updated with AHPRA" data-testid="name-correction-reason" />
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="button" size="sm" onClick={submit} disabled={saving} data-testid="name-correction-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit request
            </Button>
          </div>
        </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
